import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { HardHat, Plus, X, Trash2, KeyRound, Pencil, LayoutGrid, List } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

export default function Engineers() {
  const [engineers, setEngineers] = useState([]);
  const [perf, setPerf] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [view, setView] = useState("list");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [u, p] = await Promise.all([api.get("/users?role=engineer"), api.get("/dashboard/engineer-performance")]);
      setEngineers(u.data); setPerf(p.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const perfMap = Object.fromEntries(perf.map((p) => [p.id, p]));

  const ids = engineers.map((e) => e.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Remove ${idsArr.length} engineer(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/users/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Team</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Field Engineers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Roster with real-time workload.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border/70">
            <button data-testid="engineers-view-list" onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button data-testid="engineers-view-cards" onClick={() => setView("cards")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
          </div>
          <button data-testid="engineer-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New engineer
          </button>
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {view === "cards" && engineers.length > 0 && (
        <div className="mb-3">
          <label className="inline-flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs">
            <RowCheckbox testid="engineer-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
            Select all
          </label>
        </div>
      )}

      {view === "list" ? (
        <div className="sharp-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="engineers-list-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Name</th>
                <th className="px-4 py-2 label-eyebrow">Email / Phone</th>
                <th className="px-4 py-2 label-eyebrow text-center">Assigned</th>
                <th className="px-4 py-2 label-eyebrow text-center">In Progress</th>
                <th className="px-4 py-2 label-eyebrow text-center">Completed</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {engineers.map((e) => {
                const p = perfMap[e.id] || {};
                return (
                  <tr key={e.id} data-testid={`engineer-row-${e.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                    <td className="px-4 py-2">
                      <RowCheckbox testid={`engineer-select-${e.id}`} checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} />
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-2 font-medium"><HardHat className="h-3.5 w-3.5 text-primary" />{e.name}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      <p className="truncate">{e.email}</p>
                      <p>{e.phone || "—"}</p>
                    </td>
                    <td className="px-4 py-2 text-center font-semibold">{p.assigned ?? 0}</td>
                    <td className="px-4 py-2 text-center font-semibold">{p.in_progress ?? 0}</td>
                    <td className="px-4 py-2 text-center font-semibold text-emerald-500">{p.completed ?? 0}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button data-testid={`engineer-edit-${e.id}`} onClick={() => setEditing(e)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`engineer-reset-password-${e.id}`} title="Reset password" onClick={() => setResetting(e)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><KeyRound className="h-3.5 w-3.5" /></button>
                        <button data-testid={`engineer-delete-${e.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Remove engineer "${e.name}"?`)) return;
                            try { await api.delete(`/users/${e.id}`); await load(); }
                            catch (er) { setErr(formatApiError(er)); }
                          }}
                          className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {engineers.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-muted-foreground">No engineers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {engineers.map((e) => {
          const p = perfMap[e.id] || {};
          return (
            <div key={e.id} className={`sharp-card relative p-5 transition-colors hover:border-primary/40 ${selected.has(e.id) ? "ring-2 ring-primary/60" : ""}`}>
              <div className="absolute right-3 top-3">
                <RowCheckbox testid={`engineer-select-${e.id}`} checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} />
              </div>
              <div className="flex items-center gap-3 pr-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <HardHat className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-heading text-base font-semibold">{e.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{e.email}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <Stat label="Assigned" value={p.assigned ?? 0} />
                <Stat label="In progress" value={p.in_progress ?? 0} />
                <Stat label="Completed" value={p.completed ?? 0} />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  data-testid={`engineer-edit-${e.id}`}
                  onClick={() => setEditing(e)}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  data-testid={`engineer-reset-password-${e.id}`}
                  onClick={() => setResetting(e)}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Reset password
                </button>
                <button
                  data-testid={`engineer-delete-${e.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Remove engineer "${e.name}"?`)) return;
                    try { await api.delete(`/users/${e.id}`); await load(); }
                    catch (er) { setErr(formatApiError(er)); }
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-destructive/60 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {engineers.length === 0 && <p className="col-span-full text-sm text-muted-foreground">No engineers yet.</p>}
      </div>
      )}

      <BulkBar count={selected.size} entityLabel="engineers" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <CreateEngineerModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(false);
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setErr("Passwords do not match.");
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">Reset password — {user.name}</h3>
          <button data-testid="reset-password-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">New password (min 8 chars)</label>
            <input data-testid="reset-password-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Confirm new password</label>
            <input data-testid="reset-password-confirm" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          {ok && <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-500">Password updated.</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="reset-password-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border/60 bg-secondary/40 px-2 py-2">
      <p className="font-heading text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function CreateEngineerModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", password: "", role: "engineer" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    // Basic phone sanity — must have at least 6 digits
    const digits = (form.phone || "").replace(/\D/g, "");
    if (digits.length < 6) { setErr("Mobile number must contain at least 6 digits."); setBusy(false); return; }
    try { await api.post("/users", form); onSaved(); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">New engineer account</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5">
          <div>
            <label className="label-eyebrow mb-1.5 block">Full name *</label>
            <input data-testid="engineer-form-name" required value={form.name} onChange={set("name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Mobile number * <span className="text-muted-foreground">(engineer will sign in with this)</span></label>
            <input data-testid="engineer-form-phone" required type="tel" inputMode="tel" pattern="[0-9+\- ]{6,}" value={form.phone} onChange={set("phone")} placeholder="e.g. 9876543210" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Temporary password *</label>
            <input data-testid="engineer-form-password" required type="password" minLength={8} value={form.password} onChange={set("password")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div data-testid="engineer-form-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="engineer-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : "Create engineer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
